import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { postData } from "../../../../Services/Fetchnodeservices";

// ═════════════════════════════════════════════════════════════════════════════
// LUXURY THEME SYSTEM - Premium & Refined
// ═════════════════════════════════════════════════════════════════════════════
const getThemeStyles = (isDark) => {
  const themes = {
    dark: {
      background: "#0a0e1a",
      surface: "#141b2f",
      surfaceLighter: "#1e2747",
      border: "#2a3f5f",
      borderAccent: "#3d5a8c",
      textPrimary: "#f8fafc",
      textSecondary: "#cbd5e1",
      textMuted: "#94a3b8",
      primary: "#60a5fa",
      primaryLight: "#93c5fd",
      primaryDark: "#1e40af",
      success: "#34d399",
      successLight: "#6ee7b7",
      error: "#f87171",
      warning: "#fbbf24",
      gradientPrimary: "linear-gradient(135deg, #60a5fa 0%, #a78bfa 50%, #f472b6 100%)",
      gradientCard: "linear-gradient(135deg, #1e2747 0%, #141b2f 100%)",
      gradientSubtle: "linear-gradient(135deg, rgba(96,165,250,0.1), rgba(167,139,250,0.05))",
      shadowSm: "0 2px 8px rgba(0,0,0,0.15)",
      shadowMd: "0 8px 16px rgba(0,0,0,0.2)",
      shadowLg: "0 16px 32px rgba(0,0,0,0.25)",
      shadowXl: "0 24px 48px rgba(0,0,0,0.3)",
      glowPrimary: "0 0 20px rgba(96,165,250,0.3)",
      tooltipBg: "#1e2747",
      tooltipText: "#f8fafc",
    },
    light: {
      background: "#f8fafc",
      surface: "#ffffff",
      surfaceLighter: "#f1f5f9",
      border: "#e2e8f0",
      borderAccent: "#cbd5e1",
      textPrimary: "#0f172a",
      textSecondary: "#334155",
      textMuted: "#64748b",
      primary: "#3b82f6",
      primaryLight: "#60a5fa",
      primaryDark: "#1e40af",
      success: "#10b981",
      successLight: "#34d399",
      error: "#ef4444",
      warning: "#f59e0b",
      gradientPrimary: "linear-gradient(135deg, #3b82f6 0%, #8b5cf6 50%, #ec4899 100%)",
      gradientCard: "linear-gradient(135deg, #ffffff 0%, #f8f4ff 100%)",
      gradientSubtle: "linear-gradient(135deg, rgba(59,130,246,0.08), rgba(139,92,246,0.04))",
      shadowSm: "0 2px 8px rgba(0,0,0,0.08)",
      shadowMd: "0 8px 16px rgba(0,0,0,0.1)",
      shadowLg: "0 16px 32px rgba(0,0,0,0.12)",
      shadowXl: "0 24px 48px rgba(0,0,0,0.15)",
      glowPrimary: "0 0 20px rgba(59,130,246,0.2)",
      tooltipBg: "#1e293b",
      tooltipText: "#ffffff",
    },
  };
  return themes[isDark ? 'dark' : 'light'];
};

// ═════════════════════════════════════════════════════════════════════════════
// REFINED ICONS - Elegant SVG Icons
// ═════════════════════════════════════════════════════════════════════════════
const BackIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
    <polyline points="15 18 9 12 15 6"/>
  </svg>
);

const AddIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
    <line x1="12" y1="5" x2="12" y2="19"/>
    <line x1="5" y1="12" x2="19" y2="12"/>
  </svg>
);

const ScanIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <path d="M21 12a9 9 0 1 1-9-9m9 0v6m0-6h-6"/>
    <circle cx="12" cy="12" r="3"/>
  </svg>
);

const PDFIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
    <polyline points="14 2 14 8 20 8"/>
  </svg>
);

const UploadIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
    <polyline points="17 8 12 3 7 8"/>
    <line x1="12" y1="3" x2="12" y2="15"/>
  </svg>
);

const SearchIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
    <circle cx="11" cy="11" r="8"/>
    <line x1="21" y1="21" x2="16.65" y2="16.65"/>
  </svg>
);

const EditIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
  </svg>
);

const TrashIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="3 6 5 6 21 6"/>
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
  </svg>
);

const ChevronLeftIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <polyline points="15 18 9 12 15 6"/>
  </svg>
);

const ChevronRightIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <polyline points="9 18 15 12 9 6"/>
  </svg>
);

const VegIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
    <circle cx="12" cy="12" r="8"/>
  </svg>
);

const NonVegIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
    <rect x="6" y="6" width="12" height="12" rx="1"/>
    <circle cx="12" cy="12" r="8" fill="none" stroke="currentColor" strokeWidth="1"/>
  </svg>
);

const ImageIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
    <circle cx="8.5" cy="8.5" r="1.5"/>
    <polyline points="21 15 16 10 5 21"/>
  </svg>
);

const ViewIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
    <circle cx="12" cy="12" r="3"/>
  </svg>
);

const ReplaceIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21 2v4h-4M3 10v-4h4"/>
    <path d="M21 12a9 9 0 0 1-13.5 7.5M3 12a9 9 0 0 1 13.5-7.5"/>
  </svg>
);

const RemoveIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="3 6 5 6 21 6"/>
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
    <line x1="10" y1="11" x2="10" y2="17"/>
    <line x1="14" y1="11" x2="14" y2="17"/>
  </svg>
);

const StarIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
  </svg>
);

const ChefHatIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M18 13c0 2.21-1.79 4-4 4h-4c-2.21 0-4-1.79-4-4 0-1.86 1.28-3.43 3-3.87V8c0-2.21 1.79-4 4-4s4 1.79 4 4v1.13c1.72.44 3 2.01 3 3.87z"/>
  </svg>
);

const LoadingIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ animation: "spin 1.2s linear infinite" }}>
    <circle cx="12" cy="12" r="10"/>
    <path d="M12 2a10 10 0 0 1 10 10"/>
  </svg>
);

// ═════════════════════════════════════════════════════════════════════════════
// PREMIUM BADGE COMPONENTS
// ═════════════════════════════════════════════════════════════════════════════
const StatusBadge = ({ status, ts }) => {
  const config = {
    On: { bg: ts.success + "18", color: ts.success, icon: "✓" },
    Off: { bg: ts.textMuted + "12", color: ts.textMuted, icon: "○" },
  };
  const c = config[status] || config.Off;
  
  return (
    <span style={{
      display: "inline-flex",
      alignItems: "center",
      gap: "6px",
      padding: "4px 12px",
      borderRadius: "20px",
      background: c.bg,
      fontSize: "11px",
      fontWeight: 600,
      color: c.color,
      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      border: `1px solid ${c.color}20`,
    }}>
      <span style={{ fontSize: "9px", fontWeight: 700 }}>{c.icon}</span>
      {status}
    </span>
  );
};

const VegBadge = ({ isVeg, ts }) => {
  return (
    <span style={{
      display: "inline-flex",
      alignItems: "center",
      gap: "6px",
      padding: "4px 12px",
      borderRadius: "20px",
      fontSize: "11px",
      fontWeight: 600,
      background: isVeg ? ts.success + "18" : ts.error + "18",
      color: isVeg ? ts.success : ts.error,
      border: `1px solid ${isVeg ? ts.success + "30" : ts.error + "30"}`,
      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    }}>
      {isVeg ? <VegIcon style={{ width: "10px", height: "10px" }} /> : <NonVegIcon style={{ width: "10px", height: "10px" }} />}
      {isVeg ? "Veg" : "Non-Veg"}
    </span>
  );
};

// ═════════════════════════════════════════════════════════════════════════════
// TOOLTIP COMPONENT
// ═════════════════════════════════════════════════════════════════════════════
// ═════════════════════════════════════════════════════════════════════════════
// TOOLTIP COMPONENT - Small Font Size
// ═════════════════════════════════════════════════════════════════════════════
const Tooltip = ({ children, content, ts }) => {
  const [show, setShow] = useState(false);
  
  return (
    <div style={{ position: "relative", display: "inline-flex", alignItems: "center", gap: "4px" }}
         onMouseEnter={() => setShow(true)}
         onMouseLeave={() => setShow(false)}>
      {children}
      {show && (
        <div style={{
          position: "absolute",
          bottom: "calc(100% + 8px)",
          left: "50%",
          transform: "translateX(-50%)",
          background: ts.tooltipBg,
          color: ts.tooltipText,
          padding: "4px 8px",
          borderRadius: "6px",
          fontSize: "9px",
          fontWeight: 500,
          whiteSpace: "nowrap",
          zIndex: 1000,
          boxShadow: ts.shadowSm,
          border: `1px solid ${ts.border}`,
          pointerEvents: "none",
        }}>
          {content}
          <div style={{
            position: "absolute",
            top: "100%",
            left: "50%",
            transform: "translateX(-50%)",
            borderWidth: "4px",
            borderStyle: "solid",
            borderColor: `${ts.tooltipBg} transparent transparent transparent`,
          }} />
        </div>
      )}
    </div>
  );
};

// ═════════════════════════════════════════════════════════════════════════════
// MAIN ADMIN COMPONENT - Luxury Premium Edition
// ═════════════════════════════════════════════════════════════════════════════
const Admin = ({ isDark = true }) => {
  const navigate = useNavigate();
  const location = useLocation();
  
  const getDesignationId = () => {
    if (location.state?.designation_id) return location.state.designation_id;
    const stored = sessionStorage.getItem('selectedRestaurant');
    if (stored) {
      try { return JSON.parse(stored).designation_id; } catch (e) { return ""; }
    }
    return "";
  };
  
  const getRestaurantName = () => {
    if (location.state?.restaurantName) return location.state.restaurantName;
    const stored = sessionStorage.getItem('selectedRestaurant');
    if (stored) {
      try { return JSON.parse(stored).restaurantName; } catch (e) { return ""; }
    }
    return "";
  };
  
  const designation_id = getDesignationId();
  const restaurantName = getRestaurantName();
  
  const [menuItems, setMenuItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [activePage, setActivePage] = useState(1);
  const [totalPagesCount] = useState(9);
  
  const ts = getThemeStyles(isDark);

  // Helper functions for price calculations
  const calculateCostPrice = (sellingPrice) => {
    const price = parseFloat(String(sellingPrice).replace(/[^0-9.-]/g, '')) || 0;
    const costPrice = price * 1.2; // 20% increase
    return costPrice.toFixed(2);
  };

  const calculateCommissionAmount = (sellingPrice) => {
    const price = parseFloat(String(sellingPrice).replace(/[^0-9.-]/g, '')) || 0;
    const costPrice = price * 1.2;
    const commission = costPrice * 0.2; // 20% of cost price
    return commission.toFixed(2);
  };

  useEffect(() => {
    if (designation_id) {
      sessionStorage.setItem('selectedRestaurant', JSON.stringify({ designation_id, restaurantName }));
    }
  }, [designation_id, restaurantName]);

  const fetchMenu = async () => {
    if (!designation_id) {
      setError("No restaurant ID provided");
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await postData("resturant/Menubydesignation_id", { designation_id });
      let itemsArray = [];
      if (Array.isArray(response) && response.length > 0 && response[0].items) {
        try { itemsArray = JSON.parse(response[0].items); } catch { itemsArray = []; }
      } else if (response && response.items && typeof response.items === 'string') {
        try { itemsArray = JSON.parse(response.items); } catch { itemsArray = []; }
      } else if (Array.isArray(response)) { itemsArray = response; }
      
      const transformed = itemsArray.map((item, idx) => {
        const sellingPrice = item.price || 0;
        const costPrice = calculateCostPrice(sellingPrice);
        const commissionAmount = calculateCommissionAmount(sellingPrice);
        
        return {
          id: item.product_id || item.id || idx + 1,
          name: item.name || "Unnamed Item",
          sellingPrice: `₹${sellingPrice}`,
          sellingPriceValue: sellingPrice,
          costPrice: `₹${costPrice}`,
          costPriceValue: parseFloat(costPrice),
          commission: commissionAmount,
          commissionValue: parseFloat(commissionAmount),
          category: item.main_category || item.category || "Uncategorized",
          veg: item.is_veg === "yes" || item.is_veg === "1" || item.is_veg === 1,
          source: item.is_resale === 1 || item.is_resale === "1" ? "Resale" : "Direct",
          status: item.status === "1" || item.status === 1 ? "On" : "Off",
        };
      });
      setMenuItems(transformed);
    } catch (err) {
      setError(err.message || "Failed to fetch menu items");
    } finally { setLoading(false); }
  };
  
  useEffect(() => { designation_id ? fetchMenu() : setLoading(false); }, [designation_id]);
  
  const filteredItems = menuItems.filter(item =>
    item.name?.toLowerCase().includes(search.toLowerCase()) ||
    item.category?.toLowerCase().includes(search.toLowerCase())
  );
  
  const totalPages = Math.ceil(filteredItems.length / itemsPerPage);
  const currentItems = filteredItems.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  
  const toggleSelect = (id) => {
    const newSelected = new Set(selectedItems);
    newSelected.has(id) ? newSelected.delete(id) : newSelected.add(id);
    setSelectedItems(newSelected);
  };
  
  const toggleSelectAll = () => {
    if (selectedItems.size === currentItems.length && currentItems.length > 0) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(currentItems.map(item => item.id)));
    }
  };
  
  const handleAddItem = () => navigate("/Dashboard/AddMenuItem", { state: { designation_id, restaurantName } });
  const handleEditItem = (item) => navigate("/Dashboard/EditMenuItem", { state: { menuItem: item, designation_id, restaurantName } });
  const handleDeleteItem = async (itemId) => {
    if (window.confirm("Delete this item?")) {
      try {
        const response = await postData("menu/delete_menu_item", { product_id: itemId, designation_id });
        if (response?.success === true || response?.error === false) { fetchMenu(); alert("✓ Deleted successfully"); }
        else { alert(response?.message || "Failed"); }
      } catch { alert("Something went wrong"); }
    }
  };
  
  const handleAIScan = () => alert("🤖 AI Scan coming soon!");
  const handlePDFUpload = () => alert("📄 PDF upload coming soon!");
  const handleViewPage = (pageNum) => alert(`👀 Viewing Page ${pageNum}`);
  const handleScanPage = (pageNum) => alert(`🔍 Scanning Page ${pageNum}`);
  const handleReplacePage = (pageNum) => alert(`🔄 Replace Page ${pageNum}`);
  const handleRemovePage = (pageNum) => { if (window.confirm(`Remove Page ${pageNum}?`)) alert(`✓ Page ${pageNum} removed`); };
  const handleBulkPDFUpload = () => alert("📤 Bulk PDF upload coming soon!");
  
  const renderPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let endPage = Math.min(totalPages, startPage + maxVisible - 1);
    if (totalPages === 0) return [];
    for (let i = startPage; i <= endPage; i++) {
      pages.push(<button key={i} onClick={() => setCurrentPage(i)} style={styles.paginationPageBtn(currentPage === i)}>{i}</button>);
    }
    return pages;
  };

  const styles = {
    container: {
      minHeight: "100vh",
      background: ts.background,
      backgroundImage: `radial-gradient(circle at 20% 50%, ${ts.primaryDark}08 0%, transparent 50%), radial-gradient(circle at 80% 80%, ${ts.primaryDark}04 0%, transparent 50%)`,
      padding: "32px 40px",
      fontFamily: "'Inter', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif",
      letterSpacing: "-0.3px",
    },
    
    backButton: {
      display: "flex",
      alignItems: "center",
      gap: "10px",
      background: ts.surfaceLighter,
      border: `1px solid ${ts.border}`,
      borderRadius: "14px",
      padding: "10px 18px",
      cursor: "pointer",
      color: ts.primary,
      fontSize: "13px",
      fontWeight: 600,
      marginBottom: "32px",
      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      boxShadow: ts.shadowSm,
    },
    
    restaurantCard: {
      background: ts.surface,
      border: `1.5px solid ${ts.border}`,
      borderRadius: "24px",
      padding: "32px",
      marginBottom: "32px",
      boxShadow: ts.shadowMd,
      backgroundImage: ts.gradientCard,
      position: "relative",
      overflow: "hidden",
      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    },
    
    restaurantCardDecor: {
      position: "absolute",
      width: "300px",
      height: "300px",
      borderRadius: "50%",
      background: `radial-gradient(circle, ${ts.primary}08, transparent)`,
      top: "-100px",
      right: "-100px",
      pointerEvents: "none",
    },
    
    restaurantName: {
      fontSize: "28px",
      fontWeight: 800,
      color: ts.textPrimary,
      marginBottom: "12px",
      letterSpacing: "-0.8px",
      background: ts.gradientPrimary,
      backgroundClip: "text",
      WebkitBackgroundClip: "text",
      WebkitTextFillColor: "transparent",
    },
    
    restaurantStats: {
      display: "flex",
      gap: "12px",
      flexWrap: "wrap",
      marginBottom: "24px",
    },
    
    statBadge: {
      display: "inline-flex",
      alignItems: "center",
      gap: "8px",
      padding: "8px 16px",
      background: ts.surfaceLighter,
      borderRadius: "40px",
      fontSize: "13px",
      color: ts.textSecondary,
      fontWeight: 600,
      border: `1px solid ${ts.border}`,
      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    },
    
    actionButtons: {
      display: "flex",
      gap: "12px",
      flexWrap: "wrap",
      position: "relative",
      zIndex: 2,
    },
    
    btnPrimary: {
      background: ts.gradientPrimary,
      border: "none",
      padding: "11px 26px",
      borderRadius: "14px",
      color: "white",
      fontWeight: 700,
      display: "flex",
      alignItems: "center",
      gap: "8px",
      cursor: "pointer",
      fontSize: "13px",
      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      boxShadow: `0 4px 16px ${ts.primary}30`,
      position: "relative",
      overflow: "hidden",
    },
    
    btnSecondary: {
      background: ts.surfaceLighter,
      border: `1.5px solid ${ts.border}`,
      padding: "10px 24px",
      borderRadius: "14px",
      color: ts.primary,
      fontWeight: 600,
      display: "flex",
      alignItems: "center",
      gap: "8px",
      cursor: "pointer",
      fontSize: "13px",
      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      boxShadow: ts.shadowSm,
    },
    
    menuPagesSection: {
      background: ts.surface,
      border: `1.5px solid ${ts.border}`,
      borderRadius: "20px",
      padding: "24px",
      marginBottom: "32px",
      boxShadow: ts.shadowMd,
    },
    
    menuPagesHeader: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: "24px",
      flexWrap: "wrap",
      gap: "16px",
    },
    
    sectionTitle: {
      fontSize: "16px",
      fontWeight: 700,
      color: ts.textPrimary,
      display: "flex",
      alignItems: "center",
      gap: "10px",
      letterSpacing: "-0.3px",
    },
    
    pageActionButtons: {
      display: "flex",
      gap: "8px",
    },
    
    iconButton: {
      display: "flex",
      alignItems: "center",
      gap: "6px",
      padding: "8px 16px",
      background: ts.surfaceLighter,
      border: `1px solid ${ts.border}`,
      borderRadius: "12px",
      color: ts.primary,
      fontSize: "12px",
      fontWeight: 600,
      cursor: "pointer",
      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    },
    
    pagesGrid: {
      display: "flex",
      gap: "16px",
      flexWrap: "wrap",
    },
    
    pageItem: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: "8px",
    },
    
    pageThumbnail: (isActive) => ({
      width: "80px",
      height: "104px",
      background: isActive ? ts.gradientPrimary : ts.surfaceLighter,
      border: `2.5px solid ${isActive ? ts.primary : ts.border}`,
      borderRadius: "14px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: "pointer",
      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      boxShadow: isActive ? ts.glowPrimary : ts.shadowSm,
      position: "relative",
    }),
    
    pageNumber: (isActive) => ({
      fontSize: "14px",
      fontWeight: 700,
      color: isActive ? "white" : ts.textSecondary,
    }),
    
    pageLabel: {
      fontSize: "11px",
      color: ts.textMuted,
      fontWeight: 500,
    },
    
    searchWrapper: {
      position: "relative",
      marginBottom: "28px",
    },
    
    searchInput: {
      width: "100%",
      padding: "13px 18px 13px 48px",
      background: ts.surface,
      border: `1.5px solid ${ts.border}`,
      borderRadius: "16px",
      fontSize: "14px",
      color: ts.textPrimary,
      outline: "none",
      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      boxShadow: ts.shadowSm,
      fontWeight: 500,
    },
    
    searchIcon: {
      position: "absolute",
      left: "18px",
      top: "50%",
      transform: "translateY(-50%)",
      color: ts.primary,
      opacity: 0.7,
    },
    
    tableWrapper: {
      background: ts.surface,
      border: `1.5px solid ${ts.border}`,
      borderRadius: "20px",
      overflow: "hidden",
      boxShadow: ts.shadowMd,
      marginBottom: "24px",
    },
    
    table: {
      width: "100%",
      borderCollapse: "collapse",
      tableLayout: "fixed",
    },
    
    th: (width) => ({
      padding: "16px 12px",
      textAlign: "left",
      fontSize: "12px",
      fontWeight: 700,
      color: ts.primary,
      borderBottom: `1.5px solid ${ts.border}`,
      background: ts.surfaceLighter,
      textTransform: "uppercase",
      letterSpacing: "0.8px",
      width: width || "auto",
    }),
    
    td: {
      padding: "14px 12px",
      borderBottom: `1px solid ${ts.border}`,
      fontSize: "13px",
      color: ts.textSecondary,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    
    checkbox: {
      width: "18px",
      height: "18px",
      cursor: "pointer",
      accentColor: ts.primary,
      borderRadius: "6px",
    },
    
    imagePlaceholder: {
      width: "36px",
      height: "36px",
      borderRadius: "12px",
      background: ts.surfaceLighter,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      border: `1.5px solid ${ts.border}`,
      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    },
    
    loadingContainer: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      padding: "100px 40px",
      flexDirection: "column",
      gap: "20px",
      color: ts.textMuted,
    },
    
    errorContainer: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      padding: "80px 40px",
      flexDirection: "column",
      gap: "16px",
      color: ts.error,
      background: ts.surface,
      borderRadius: "20px",
      marginTop: "20px",
      border: `1.5px solid ${ts.error}30`,
    },
    
    noDataText: {
      textAlign: "center",
      padding: "80px 40px",
      color: ts.textMuted,
      fontSize: "14px",
      fontWeight: 500,
    },
    
    pagination: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      padding: "18px 20px",
      borderTop: `1.5px solid ${ts.border}`,
      background: ts.surfaceLighter,
    },
    
    paginationInfo: {
      fontSize: "12px",
      color: ts.textMuted,
      fontWeight: 500,
    },
    
    paginationButtons: {
      display: "flex",
      gap: "8px",
    },
    
    paginationBtn: (disabled) => ({
      width: "36px",
      height: "36px",
      borderRadius: "10px",
      border: `1.5px solid ${ts.border}`,
      background: ts.surface,
      color: disabled ? ts.textMuted : ts.textSecondary,
      cursor: disabled ? "not-allowed" : "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      opacity: disabled ? 0.5 : 1,
    }),
    
    paginationPageBtn: (isActive) => ({
      width: "36px",
      height: "36px",
      borderRadius: "10px",
      border: isActive ? `1.5px solid ${ts.primary}` : `1.5px solid ${ts.border}`,
      background: isActive ? ts.primary : ts.surface,
      color: isActive ? "white" : ts.textMuted,
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontWeight: isActive ? 700 : 500,
      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      boxShadow: isActive ? `0 0 16px ${ts.primary}40` : "none",
    }),
    
    uploadSection: {
      marginTop: "24px",
      padding: "32px",
      background: ts.surface,
      border: `2.5px dashed ${ts.borderAccent}`,
      borderRadius: "20px",
      textAlign: "center",
      cursor: "pointer",
      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      boxShadow: ts.shadowSm,
    },
    
    uploadArea: {
      padding: "12px",
      cursor: "pointer",
    },
    
    uploadText: {
      fontSize: "13px",
      color: ts.textMuted,
      marginTop: "12px",
      fontWeight: 500,
    },
    
    actionBtn: {
      background: "none",
      border: "none",
      cursor: "pointer",
      color: ts.primary,
      padding: "6px",
      borderRadius: "10px",
      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      opacity: 0.7,
    },
    
    cpCell: {
      display: "flex",
      flexDirection: "column",
      alignItems: "flex-start",
      gap: "6px",
    },
    
    cpPrice: {
      fontWeight: 700,
      color: ts.textPrimary,
      fontSize: "13px",
    },
    
    commissionValue: {
      fontSize: "11px",
      fontWeight: 600,
      color: ts.warning,
      background: ts.warning + "15",
      padding: "2px 8px",
      borderRadius: "12px",
      display: "inline-block",
      cursor: "pointer",
    },
    
    priceCell: {
      display: "flex",
      flexDirection: "column",
      alignItems: "flex-start",
      gap: "2px",
    },
    
    sellingPrice: {
      fontWeight: 700,
      color: ts.textPrimary,
      fontSize: "13px",
    },
    
    priceLabel: {
      fontSize: "9px",
      color: ts.textMuted,
      fontWeight: 500,
    },
  };

  return (
    <div style={styles.container}>
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes slideIn { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes float { 0%, 100% { transform: translateY(0px); } 50% { transform: translateY(-8px); } }
        
        button {
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }
        
        button:hover:not(:disabled) {
          transform: translateY(-3px);
        }
        
        button:active:not(:disabled) {
          transform: translateY(-1px);
        }
        
        input:focus {
          border-color: ${ts.primary} !important;
          box-shadow: 0 0 0 3px ${ts.primary}15 !important;
        }
        
        tr:hover {
          background-color: ${ts.primary}06 !important;
        }
      `}</style>

      <button style={styles.backButton} onClick={() => navigate(-1)}>
        <BackIcon /> Back to Restaurants
      </button>

      {/* Restaurant Header Card - Premium */}
      <div style={styles.restaurantCard}>
        <div style={styles.restaurantCardDecor} />
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: "16px", marginBottom: "20px", position: "relative", zIndex: 2 }}>
          <div style={{ flex: 1 }}>
            <h2 style={styles.restaurantName}>
              {restaurantName || "Restaurant Menu"}
              {designation_id && <span style={{ fontSize: "13px", color: ts.textMuted, marginLeft: "12px", fontWeight: 500 }}>#{designation_id}</span>}
            </h2>
            <div style={styles.restaurantStats}>
              <span style={styles.statBadge}><ChefHatIcon /> {menuItems.length} <strong>Items</strong></span>
              <span style={styles.statBadge}>💰 <strong style={{ color: ts.success }}>20%</strong> Commission on CP</span>
              <span style={styles.statBadge}>⭐ <strong>4.8</strong> (128 reviews)</span>
            </div>
          </div>
          <div style={{ display: "flex", gap: "4px", alignItems: "center", padding: "8px 14px", background: ts.surfaceLighter, borderRadius: "40px", border: `1px solid ${ts.border}` }}>
            {[...Array(5)].map((_, i) => (<StarIcon key={i} style={{ color: i < 4 ? "#fbbf24" : ts.textMuted, width: "16px", height: "16px" }} />))}
          </div>
        </div>
        <div style={styles.actionButtons}>
          <button style={styles.btnPrimary} onClick={handleAddItem}><AddIcon /> Add New Item</button>
          <button style={styles.btnSecondary} onClick={handleAIScan}><ScanIcon /> AI Scan</button>
          <button style={styles.btnSecondary} onClick={handlePDFUpload}><PDFIcon /> PDF Upload</button>
        </div>
      </div>

      {/* Menu Pages Section */}
      <div style={styles.menuPagesSection}>
        <div style={styles.menuPagesHeader}>
          <h3 style={styles.sectionTitle}>📄 Menu Pages Manager</h3>
          <div style={styles.pageActionButtons}>
            <button style={styles.iconButton} onClick={() => handleViewPage(activePage)}><ViewIcon /> View</button>
            <button style={styles.iconButton} onClick={() => handleScanPage(activePage)}><ScanIcon /> Scan</button>
            <button style={styles.iconButton} onClick={() => handleReplacePage(activePage)}><ReplaceIcon /> Replace</button>
            <button style={styles.iconButton} onClick={() => handleRemovePage(activePage)}><RemoveIcon /> Remove</button>
          </div>
        </div>
        <div style={styles.pagesGrid}>
          {Array.from({ length: totalPagesCount }, (_, i) => i + 1).map((pageNum) => (
            <div key={pageNum} style={styles.pageItem}>
              <div style={styles.pageThumbnail(activePage === pageNum)} onClick={() => setActivePage(pageNum)}>
                <span style={styles.pageNumber(activePage === pageNum)}>{pageNum}</span>
              </div>
              <span style={styles.pageLabel}>Page {pageNum}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Search Bar */}
      <div style={styles.searchWrapper}>
        <span style={styles.searchIcon}><SearchIcon /></span>
        <input type="text" style={styles.searchInput} placeholder="Search menu items by name or category..." value={search} onChange={(e) => setSearch(e.target.value)} />
      </div>

      {/* Menu Items Table */}
      <div style={styles.tableWrapper}>
        {loading ? (
          <div style={styles.loadingContainer}>
            <LoadingIcon />
            <span style={{ fontSize: "14px" }}>Loading menu items...</span>
          </div>
        ) : error ? (
          <div style={styles.errorContainer}>
            <span style={{ fontSize: "16px" }}>⚠️ {error}</span>
            <button onClick={fetchMenu} style={styles.btnSecondary}>Retry</button>
          </div>
        ) : (
          <>
            <div style={{ overflowX: "auto" }}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th("36px")}>
                      <input
                        type="checkbox"
                        style={styles.checkbox}
                        checked={selectedItems.size === currentItems.length && currentItems.length > 0}
                        onChange={toggleSelectAll}
                      />
                    </th>
                    <th style={styles.th("45px")}>#</th>
                    <th style={styles.th("50px")}>Image</th>
                    <th style={styles.th("160px")}>Item Name</th>
                    <th style={styles.th("85px")}>menu<br />Price</th>
                    <th style={styles.th("100px")}>customer<br />Price</th>
                   <th style={styles.th("80px")}>
  Zaptodoor <br />
  Commission
</th>
                    <th style={styles.th("120px")}>Category</th>
                    <th style={styles.th("80px")}>Type</th>
                    <th style={styles.th("65px")}>Source</th>
                    <th style={styles.th("65px")}>Status</th>
                    <th style={styles.th("80px")}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {currentItems.length === 0 ? (
                    <tr>
                      <td colSpan={12} style={styles.noDataText}>
                        🍽️ No menu items found. Click "Add Item" to get started!
                       </td>
                    </tr>
                  ) : (
                    currentItems.map((item, idx) => (
                      <tr
                        key={item.id}
                        style={{
                          background: selectedItems.has(item.id) ? ts.primary + "08" : "transparent",
                        }}
                      >
                        <td style={styles.td}>
                          <input
                            type="checkbox"
                            style={styles.checkbox}
                            checked={selectedItems.has(item.id)}
                            onChange={() => toggleSelect(item.id)}
                          />
                        </td>
                        <td style={styles.td}>{(currentPage - 1) * itemsPerPage + idx + 1}</td>
                        <td style={styles.td}>
                          <div style={styles.imagePlaceholder}>
                            <ImageIcon style={{ width: "14px", height: "14px" }} />
                          </div>
                        </td>
                        <td style={styles.td}>
                          <span style={{ fontWeight: 600, color: ts.textPrimary }}>
                            {item.name}
                          </span>
                        </td>
                        <td style={styles.td}>
                          <div style={styles.priceCell}>
                            <span style={styles.sellingPrice}>{item.sellingPrice}</span>
                            <span style={styles.priceLabel}>Customer Price</span>
                          </div>
                        </td>
                        <td style={styles.td}>
                          <div style={styles.cpCell}>
                            <span style={styles.cpPrice}>{item.costPrice}</span>
                          </div>
                        </td>
                        <td style={styles.td}>
                          <Tooltip content="20% Commission" ts={ts}>
                            <span style={styles.commissionValue}>
                              ₹{item.commission}
                            </span>
                          </Tooltip>
                        </td>
                        <td style={styles.td}>
                          <span style={{
                            padding: "5px 12px",
                            background: ts.surfaceLighter,
                            borderRadius: "20px",
                            fontSize: "11px",
                            fontWeight: 600,
                            border: `1px solid ${ts.border}`,
                          }}>
                            {item.category}
                          </span>
                        </td>
                        <td style={styles.td}>
                          <VegBadge isVeg={item.veg} ts={ts} />
                        </td>
                        <td style={styles.td}>
                          <span style={{ fontWeight: 600, color: ts.textSecondary }}>
                            {item.source}
                          </span>
                        </td>
                        <td style={styles.td}>
                          <StatusBadge status={item.status} ts={ts} />
                        </td>
                        <td style={styles.td}>
                          <div style={{ display: "flex", gap: "8px" }}>
                            <button
                              style={styles.actionBtn}
                              title="Edit"
                              onClick={() => handleEditItem(item)}
                            >
                              <EditIcon />
                            </button>
                            <button
                              style={styles.actionBtn}
                              title="Delete"
                              onClick={() => handleDeleteItem(item.id)}
                            >
                              <TrashIcon />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div style={styles.pagination}>
                <span style={styles.paginationInfo}>
                  Showing {(currentPage - 1) * itemsPerPage + 1}–{Math.min(currentPage * itemsPerPage, filteredItems.length)} of {filteredItems.length} items
                </span>
                <div style={styles.paginationButtons}>
                  <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} style={styles.paginationBtn(currentPage === 1)}>
                    <ChevronLeftIcon />
                  </button>
                  {renderPageNumbers()}
                  <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} style={styles.paginationBtn(currentPage === totalPages)}>
                    <ChevronRightIcon />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Upload Section */}
      <div style={styles.uploadSection} onClick={handleBulkPDFUpload}>
        <div style={styles.uploadArea}>
          <UploadIcon style={{ width: "36px", height: "36px", color: ts.primary, opacity: 0.8 }} />
          <div style={styles.uploadText}>
            <strong style={{ color: ts.textPrimary, fontSize: "14px", display: "block" }}>📤 Bulk PDF Upload</strong>
            <span style={{ fontSize: "12px" }}>Drag and drop PDF files or click to browse</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Admin;